import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Mail, Instagram, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReserveButton from './ReserveButton';
import { handleImageError } from '@/utils/imageHandling';
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

// Mock search data
const searchData = {
  tours: [
    { name: 'Brooklyn Heights Cat Tour', path: '/tours/brooklyn-heights' },
    { name: 'Financial District Cat Tour', path: '/tours/financial-district' },
    { name: 'Lower East Side Cat Tour', path: '/tours/lower-east-side' },
    { name: 'Private Cat Tours', path: '/tours/private-tours' }
  ],
  pages: [
    { name: 'About Us', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
    { name: 'Press Kit', path: '/press-kit' },
    { name: 'Book Now', path: '/booking' },
    { name: 'In the News', path: '/in-the-news' },
    { name: 'Site Map', path: '/sitemap' }
  ]
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{name: string, path: string, type?: string}>>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const navbarRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [menuHeight, setMenuHeight] = useState('100vh');

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Calculate available height for mobile menu
  useEffect(() => {
    if (navbarRef.current) {
      const navbarHeight = navbarRef.current.offsetHeight;
      setMenuHeight(`calc(100vh - ${navbarHeight}px)`);
    }
  }, [isScrolled]);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setSearchQuery('');
  }, [location.pathname]);
  
  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Focus search input when search is opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isSearchOpen) setIsSearchOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isMenuOpen) setIsMenuOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.length > 1) {
      // Search tours
      const matchingTours = searchData.tours
        .filter(tour => tour.name.toLowerCase().includes(query.toLowerCase()))
        .map(tour => ({...tour, type: 'tour'}));
      
      // Search pages
      const matchingPages = searchData.pages
        .filter(page => page.name.toLowerCase().includes(query.toLowerCase()))
        .map(page => ({...page, type: 'page'}));
      
      setSearchResults([...matchingTours, ...matchingPages]);
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      navigate(searchResults[0].path);
      toggleSearch();
    }
  };

  const handleResultClick = (path: string) => {
    navigate(path);
    toggleSearch();
  };

  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Tours', path: '/tours' },
    { name: 'Blog', path: '/blog' },
    { name: 'In the News', path: '/in-the-news' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header 
      ref={navbarRef}
      className={cn(
        'sticky w-full top-0 z-50 transition-all duration-500',
        isScrolled 
          ? 'bg-[#F5F5DC]/95 shadow-elegant supports-backdrop-blur:backdrop-blur-sm h-16' 
          : 'bg-transparent h-20'
      )}
      role="banner"
    >
      <div className="container mx-auto px-4 h-full">
        <nav className="flex items-center justify-between h-full" aria-label="Main navigation">
          {/* Logo - Updated for better mobile display */}
          <Link to="/" className="flex items-center group" aria-label="Cats About Town Tours - Home">
            <img 
              src="/lovable-uploads/a7814c71-1569-43b8-8a9c-e24f811be427.png" 
              alt="Cats About Town Tours Logo" 
              className={cn(
                "transition-transform duration-300 object-contain",
                isScrolled ? "h-12 w-auto" : "h-14 w-auto"
              )}
              width="auto"
              height={isScrolled ? "48" : "56"}
              loading="eager"
              onError={(e) => handleImageError(e)}
            />
            <span className="text-xl font-montserrat font-bold hidden sm:block ml-3">
              <span className="text-[#8D6E63]">Cats</span>
              <span className="text-[#3E2723]"> About Town Tours</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 lg:gap-6">
            {navLinks.map(link => (
              <Link 
                key={link.path}
                to={link.path}
                className={cn(
                  'px-3 py-2 font-montserrat font-medium transition-all duration-300 relative group',
                  location.pathname === link.path 
                    ? "text-[#657C56] font-semibold" 
                    : "text-[#3E2723] hover:text-[#657C56]"
                )}
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                {link.name}
                <span 
                  className={cn(
                    "absolute left-0 bottom-0 w-full h-[1px] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100",
                    location.pathname === link.path ? "bg-[#657C56] scale-x-100" : "bg-[#657C56]"
                  )}
                  aria-hidden="true"
                ></span>
              </Link>
            ))}
            <button
              onClick={toggleSearch}
              className="p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#657C56] text-[#3E2723] hover:text-[#657C56]"
              aria-expanded={isSearchOpen}
              aria-label="Search website"
            >
              <Search size={20} aria-hidden="true" />
            </button>
            <ReserveButton 
              className="ml-4"
              showCalendarIcon={false}
            />
          </div>

          {/* Mobile Menu Button - Added more space between buttons */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={toggleSearch}
              className="p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#657C56] text-[#3E2723] hover:text-[#657C56]"
              aria-expanded={isSearchOpen}
              aria-label="Search website"
            >
              <Search size={20} aria-hidden="true" />
            </button>
            <ReserveButton 
              className="text-sm py-2 px-3"
              showCalendarIcon={false}
            />
            <button 
              onClick={toggleMenu}
              className="p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#657C56] text-[#3E2723] hover:text-[#657C56] ml-1"
              aria-expanded={isMenuOpen}
              aria-label="Toggle menu"
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Search Overlay */}
      <div 
        className={cn(
          'fixed inset-0 bg-[#3E2723]/80 supports-backdrop-blur:backdrop-blur-md z-40 transition-opacity duration-300',
          isSearchOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        aria-hidden={!isSearchOpen}
      >
        <div className="container mx-auto px-4 pt-32">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-[#3E2723]">Search Cats About Town Tours</h2>
              <button 
                onClick={toggleSearch}
                className="text-[#3E2723] hover:text-[#657C56] transition-colors p-2 rounded-full"
                aria-label="Close search"
              >
                <X size={24} aria-hidden="true" />
              </button>
            </div>
            
            <form onSubmit={handleSearchSubmit} role="search">
              <div className="relative">
                <input 
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full border-2 border-[#8D6E63] rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[#657C56] focus:border-[#657C56] text-lg"
                  placeholder="Search tours, blog posts, pages..."
                  aria-label="Search website content"
                  autoComplete="off"
                />
                <button 
                  type="submit"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#8D6E63]"
                  aria-label="Submit search"
                >
                  <Search size={24} aria-hidden="true" />
                </button>
              </div>
            </form>
            
            {searchQuery.length > 1 && (
              <div className="mt-4">
                <div role="region" aria-label="Search results" className="max-h-60 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {searchResults.map((result, index) => (
                        <li key={`${result.path}-${index}`}>
                          <button
                            className="w-full text-left py-3 px-2 hover:bg-[#FDF6ED] transition-colors rounded flex items-center justify-between"
                            onClick={() => handleResultClick(result.path)}
                          >
                            <span>{result.name}</span>
                            {result.type && (
                              <span className="text-xs px-2 py-1 bg-[#F5F5DC] text-[#657C56] rounded-full">
                                {result.type === 'tour' ? 'Tour' : 'Page'}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="py-3 text-center text-gray-500">No results found for "{searchQuery}"</p>
                  )}
                </div>
                
                <div className="mt-4 pt-3 border-t border-gray-200 text-sm text-center text-gray-500">
                  Press Enter to search or click on a result
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div 
        id="mobile-menu"
        className={cn(
          'fixed inset-0 bg-[#F5F5DC]/95 supports-backdrop-blur:backdrop-blur-md z-40 transition-all duration-500 ease-in-out md:hidden overflow-auto',
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{ top: navbarRef.current ? navbarRef.current.offsetHeight : '70px', maxHeight: menuHeight }}
        aria-hidden={!isMenuOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        <nav className="py-4 px-6 flex flex-col">
          <ul className="space-y-1">
            {navLinks.map(link => (
              <li key={link.path}>
                <Link 
                  to={link.path}
                  className={cn(
                    'py-3 px-4 border-b border-[#8D6E63]/10 text-lg font-montserrat font-medium transition-all duration-300 block',
                    location.pathname === link.path 
                      ? 'text-[#657C56] font-semibold' 
                      : 'text-[#3E2723] hover:text-[#657C56]'
                  )}
                  onClick={closeMenu}
                  aria-current={location.pathname === link.path ? 'page' : undefined}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
          <ReserveButton 
            className="mt-6 text-center" 
            fullWidth={true}
          />
          
          {/* Social links in mobile menu */}
          <div className="mt-8 flex justify-center gap-4" aria-label="Social Links">
            <a 
              href="mailto:catsabouttowntour@gmail.com"
              className="p-3 bg-[#8D6E63]/10 rounded-full text-[#3E2723] hover:bg-[#8D6E63]/20 transition-all duration-300"
              aria-label="Email us"
            >
              <Mail size={20} aria-hidden="true" />
            </a>
            <a 
              href="https://www.instagram.com/catsabouttowntours/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-[#8D6E63]/10 rounded-full text-[#3E2723] hover:bg-[#8D6E63]/20 transition-all duration-300"
              aria-label="Follow us on Instagram"
            >
              <Instagram size={20} aria-hidden="true" />
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
